import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import { api } from '~/api/client'
import { useCalendar } from '~/calendar/Data'
import { useViewer } from '~/Viewer'

import { BillSection } from './BillSection'
import { Briefing } from './Briefing'
import { EditorPanel } from './EditorPanel'
import { EventSheet } from './EventSheet'
import { seedIn } from './seed'
import { WhosGoing } from './WhosGoing'

/**
 * A gig, or an act, opened over whatever page you were reading.
 *
 * `/e/:id` used to render the calendar with the sheet on top, so pressing a card on the
 * front page took you to the calendar — a page you had not asked for, in a view you had not
 * chosen, with the highlights you were reading gone. Raised more than once, and rightly:
 * opening a thing should not move you.
 *
 * It is rendered beside the routes rather than by one, and `App` keeps the page underneath
 * from the location the link was pressed on. A cold visit to `/e/<id>` has no page
 * underneath, so it gets the front door, which is what a cold visit gets everywhere else
 * here.
 */
export function Overlay({ kind }: { kind: 'event' | 'briefing' }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const here = useLocation()
  const asked = useViewer()
  // `undefined` is "not answered yet"; the event sheet only cares about signed-in or not.
  const me = asked ?? null
  const rows = useCalendar()

  /*
   * No `rows.load()` here, deliberately.
   *
   * The sheet needed the calendar's rows for one field — which of your friends are going —
   * so opening a gig from the front page fetched all 7,247 of them. That was 400ms on a
   * press that should feel instant, and it got worse rather than better when the calendar
   * started asking for windows: `load()` with no argument means the whole thing.
   *
   * `/event/<id>` carries `friends` now. Everything else here is either in that payload or
   * is a write, so the sheet no longer reads the calendar at all.
   */

  /**
   * Back to the page underneath, or to the front page when there is none.
   *
   * `-1` is right whenever this sheet was opened from inside the app: the page underneath is
   * wherever they were and only history knows. It is wrong for a cold arrival, and wrong in
   * two different ways depending on what came before.
   *
   * Driving it in Chrome rather than jsdom is what separated them. A pasted `/e/<id>` in a
   * tab that has been somewhere else has a previous entry, and `-1` spends it: closing took
   * the reader to `about:blank`, off the site. In a genuinely fresh tab there is nothing to
   * go back to, so `-1` did nothing at all — the sheet stayed, the address still said
   * `/e/<id>`, and pressing × again did nothing either.
   *
   * `history.state.idx` tells the two apart. React Router maintains it, and it is `0`
   * exactly when this entry is the first in the session — which is the whole question. An
   * earlier attempt asked `state.background` instead, which is a different question: it says
   * whether an opener passed one, not whether there is anywhere to go back to.
   */
  /**
   * Open another gig in the sheet — an "also playing" date, or a same-night candidate.
   *
   * A push, not a replace. It was a replace, which meant gig A's history entry was
   * overwritten by gig B: Back could not reach A, because as far as the browser was
   * concerned A had never happened. A sheet-to-sheet hop is exactly the move somebody wants
   * to undo — you go and look at the other date and then come back to the one you were
   * deciding about — and it was the one move the site could not undo.
   *
   * The page underneath travels with it. `App` reads `state.background` to decide what to
   * draw behind the sheet, and neither the old call nor a plain push passed one — so hopping
   * from a gig opened on the calendar put the *front page* behind the second sheet. Carried
   * explicitly rather than left to the fallback, which is only right for a cold arrival.
   */
  const openEvent = (event: string) =>
    navigate(`/e/${event}`, {
      state: { background: (here.state as { background?: unknown } | null)?.background ?? here },
    })

  /**
   * Open a gig over the briefing, which is a page and not a sheet.
   *
   * `here` itself, never unwrapped. The unwrap in `openEvent` is right from inside a sheet,
   * where `here` is the sheet's own address and its `state.background` is the page beneath
   * it. On a page there is nothing to unwrap, and whatever is in `state.background` is
   * stale: the masthead used to pass one to `/brief`, and a browser keeps an entry's state
   * across reloads, so entries made before that stopped still carry it. Unwrapping it drew
   * that page — usually the highlights — under a gig opened from the briefing, and unmounted
   * the briefing to do it.
   *
   * This is `AppLink`'s rule, an opener on a page carries `{ background: here }`, applied by
   * hand because the briefing's rows open on a click rather than through a link.
   */
  const openOverPage = (event: string) => navigate(`/e/${event}`, { state: { background: here } })

  const shut = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx
    if (typeof idx === 'number' && idx > 0) navigate(-1)
    else navigate('/', { replace: true })
  }

  if (kind === 'briefing') {
    // `undefined` is "not answered yet" and `null` is "nobody". Drawing the sign-in during
    // the gap would send a signed-in reader to a login they do not need.
    if (asked === undefined) return null
    // A sign-in for a stranger, the calendar for everybody else who is not curating. Both
    // beat what this did before, which was render nothing at all — so a briefing opened
    // without the right account looked like a button that did not work.
    if (asked === null) {
      return <Navigate to={`/login?next=${encodeURIComponent('/brief')}`} replace />
    }
    // The front door, which is what every cold arrival here gets — see the note on `App`.
    //
    // Reached by an editor in normie mode too, and the bar no longer offers the button
    // there: `useWaiting` zeroes the count while the switch is on, so the only way here is
    // a typed address, and the front door is the right answer to one.
    if (!asked.isEditor) return <Navigate to="/" replace />
    return <Briefing me={asked} onClose={shut} onOpen={openOverPage} />
  }

  return (
    <EventSheet
      id={id}
      // The row that was clicked, when one was and it was in this page load. A sheet-to-sheet
      // hop through `openEvent` carries none, so the gig being left is never drawn as a seed
      // for the one being opened.
      seed={seedIn(here.state, id)}
      me={me}
      onClose={shut}
      onVenue={slug => navigate(`/v/${slug}`)}
      // Only ever called for a gig whose line-up has been dropped for repeating the title,
      // and it goes exactly where the dropped bill's name went.
      onArtist={artist => navigate(`/a/${artist}`)}
      // Who is going sits with the night, above the blurb: it is a fact about the evening.
      actions={({ detail }) => (
        <WhosGoing
          friends={detail.friends ?? []}
          otherDates={[]}
          onOpen={openEvent}
          sections={['going']}
          past={Boolean(detail.event.past)}
        />
      )}
    >
      {({ detail, reload, patch }) => (
        <>
          {/*
            * No line-up when it would only say the title again.
            *
            * `title_act` is the server's answer to "does this bill add anything" — one act,
            * not a placeholder, same name as the night. 1,777 upcoming gigs qualify, all of
            * them reading `Marina Sena` and then, underneath, a section headed Line-up
            * containing Marina Sena.
            *
            * The editor keeps it. Every control that can *fix* a bill is in this section —
            * drop an act, add one the feed missed, follow the headliner — so hiding it from
            * the person whose job that is would remove the only way to correct the very
            * thing that made it redundant. They are reading it as a queue, not as a page.
            */}
          {detail.title_act !== null && !me?.isEditor ? null : (
          <BillSection
            acts={detail.acts}
            dropped={detail.dropped}
            me={me}
            favourites={detail.favourites}
            otherDates={detail.other_dates}
            onArtist={artist => navigate(`/a/${artist}`)}
            /*
             * Patched, not reloaded.
             *
             * `rows.star` patches the *calendar's* entries; this sheet's ★ reads
             * `detail.favourites`, which is `EventSheet`'s own state — so without something
             * here the write landed and the star did not flip. A `reload` did fix that, and
             * cost a refetch of the whole event on every press: the artwork, the bill and
             * the tickets all redrew to move one glyph, which reads as the page flashing.
             *
             * The three below still reload, and should. Adding a name to a line-up comes
             * back with an id and a photograph the browser cannot invent; a follow is one
             * number in a list the browser already holds.
             */
            onStar={(artist, on) => {
              const mark = (lit: boolean) =>
                patch(was => ({
                  ...was,
                  favourites: lit
                    ? [...was.favourites.filter(id => id !== artist), artist]
                    : was.favourites.filter(id => id !== artist),
                }))
              mark(on)
              // The calendar's rows are patched only on success; this star is patched
              // first, so when the write does not take it is put back — the sentence
              // `attempt` has shown is otherwise beside a star that says the opposite.
              void rows.star(artist, on).then(took => {
                if (!took) mark(!on)
              })
            }}
            /*
             * `name`, `artist`, `on` — the keys `/bill` actually reads.
             *
             * The port sent `add`, `add_artist` and `drop`, which the endpoint has never
             * looked at: it takes `body.get("name")` and `body.get("artist")`, found
             * neither, and did nothing. The request went out and came back 200, so pressing
             * Add looked like a control that was simply ignoring you — and removing an act
             * had been broken the same way without anybody pressing it.
             */
            onAdd={name => void api.bill({ id, name }).then(reload)}
            onPick={artist => void api.bill({ id, artist }).then(reload)}
            onDrop={artist => void api.bill({ id, artist, on: false }).then(reload)}
            onFind={query => api.findActs(query).then(found => found.artists)}
          />
          )}
          {/* Where else the acts play goes under the bill, because it only means anything
              once you know who is on it. */}
          {/* Where else the acts play. The same-night pile used to be drawn here too and is
              not any more — it lives in the editor panel below, which is the copy that can
              actually fold one listing into another. */}
          <WhosGoing
            friends={[]}
            otherDates={detail.other_dates}
            onOpen={openEvent}
            sections={['context']}
          />
          {me?.isEditor ? (
            <EditorPanel
              me={me}
              event={detail}
              sameNight={detail.same_night}
              onSave={patch => void api.edit({ id, ...patch })}
              onMerge={into => void api.merge(id, into)}
              onRefuse={reason => void api.hide(id, reason)}
              onDelete={() => void api.hide(id, null)}
              onRestore={() => void api.hide(id, 'restore')}
              onFind={query => api.findEvents(query).then(found => found.events)}
              onOpen={openEvent}
            />
          ) : null}
        </>
      )}
    </EventSheet>
  )
}
